import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../../services/tokenService';
import { FORMES_JURIDIQUES } from '../constants';

const API = '/api';

/**
 * Normalise un label de forme juridique reçu de l'API SIRET
 * vers une valeur acceptée par le Select frontend (FORMES_JURIDIQUES).
 *
 * Couvre les cas :
 *  - Label déjà court : "SAS" → "SAS"
 *  - Label INSEE long : "SAS, société par actions simplifiée" → "SAS"
 *  - Label "Entrepreneur individuel" → "EI"
 *  - Variantes coopératives → SCOP/SCIC SARL/SA/SAS
 *  - Inconnu → "Autre" (au lieu de laisser le Select vide)
 */
const normalizeFormeJuridique = (raw) => {
  if (!raw) return '';
  const value = String(raw).trim();
  if (FORMES_JURIDIQUES.includes(value)) return value;
  const upper = value.toUpperCase();

  // Recherches par mots-clés (ordre = priorité)
  if (/\bEIRL\b/.test(upper)) return 'EIRL';
  if (/ENTREPRENEUR\s+INDIVIDUEL|MICRO[- ]?ENTREPRISE|AUTO[- ]?ENTREPRENEUR/.test(upper)) return 'EI';
  if (/SCOP/.test(upper)) {
    if (/SAS/.test(upper)) return 'SCOP SAS';
    if (/SA(?!RL)/.test(upper)) return 'SCOP SA';
    return 'SCOP SARL';
  }
  if (/SCIC/.test(upper)) {
    if (/SAS/.test(upper)) return 'SCIC SAS';
    if (/SA(?!RL)/.test(upper)) return 'SCIC SA';
    return 'SCIC SARL';
  }
  if (/COOP(ERATIVE)?\s+D[' ]ACTIVITE/.test(upper)) return "CAE (Cooperative d'Activite et d'Emploi)";
  if (/SASU/.test(upper)) return 'SASU';
  if (/EURL/.test(upper)) return 'EURL';
  if (/SAS\b/.test(upper)) return 'SAS';
  if (/SARL/.test(upper) || /SOCIETE\s+A\s+RESPONSABILITE\s+LIMITEE/.test(upper)) return 'SARL';
  if (/SA\b/.test(upper) || /SOCIETE\s+ANONYME/.test(upper)) return 'SA';
  if (/ASSOC|LOI\s+1901/.test(upper)) return 'Association';
  if (/^EI$/.test(upper)) return 'EI';
  return 'Autre';
};

export default function useSiretValidation(formData, setFormData) {
  const [siretValidation, setSiretValidation] = useState({
    isValidating: false,
    isValid: null,
    message: '',
    companyData: null
  });

  const validateSiretApi = useCallback(async (siret) => {
    const siretClean = siret?.replace(/\s/g, '') || '';
    if (siretClean.length !== 14 || !/^\d{14}$/.test(siretClean)) {
      setSiretValidation({ isValidating: false, isValid: null, message: '', companyData: null });
      return;
    }

    setSiretValidation(prev => ({ ...prev, isValidating: true }));

    try {
      const token = getToken();
      const response = await axios.get(`${API}/siret/validate/${siretClean}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;

      setSiretValidation({
        isValidating: false,
        isValid: data.format_valid,
        message: data.validation_message,
        companyData: data.company_data
      });

      if (data.company_data) {
        const normalizedForme = normalizeFormeJuridique(data.company_data.forme_juridique);
        setFormData(prev => ({
          ...prev,
          raison_sociale: data.company_data.raison_sociale || prev.raison_sociale,
          code_naf: data.company_data.code_naf || prev.code_naf,
          forme_juridique: normalizedForme || prev.forme_juridique,
          adresse_entreprise: data.company_data.adresse || prev.adresse_entreprise,
          code_postal: data.company_data.code_postal || prev.code_postal,
          ville: data.company_data.ville || prev.ville,
          effectif: data.company_data.effectif || prev.effectif,
          date_creation_entreprise: data.company_data.date_creation || prev.date_creation_entreprise,
          // Représentant légal — issu de l'API (champ `dirigeants`)
          nom_gerant: data.company_data.nom_gerant || prev.nom_gerant,
          prenom_gerant: data.company_data.prenom_gerant || prev.prenom_gerant,
          fonction_gerant: data.company_data.fonction_gerant || prev.fonction_gerant,
        }));
        toast.success('Donnees entreprise recuperees automatiquement');
      }
    } catch {
      setSiretValidation({
        isValidating: false,
        isValid: null,
        message: 'Erreur de validation',
        companyData: null
      });
    }
  }, [setFormData]);

  // Debounce SIRET validation
  useEffect(() => {
    const siretClean = formData.siret?.replace(/\s/g, '') || '';
    if (siretClean.length === 14) {
      const timeoutId = setTimeout(() => validateSiretApi(siretClean), 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSiretValidation({ isValidating: false, isValid: null, message: '', companyData: null });
    }
  }, [formData.siret, validateSiretApi]);

  return { siretValidation };
}
